import { toLdsDdMmYyyy } from "../ldsDealHelpers";
import { normalizeSearchResultDate } from "../searchResultsStaleGuard";

export const HOTEL_SEARCH_DATES_TRACE = "[HOTEL_SEARCH_DATES_TRACE]";

const firstNonEmpty = (...values) => {
  for (const value of values) {
    if (value != null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return null;
};

/**
 * @param {unknown} value
 * @returns {string|null} DD/MM/YYYY when parseable
 */
export function normalizeHotelSearchDisplayDate(value) {
  if (value == null || String(value).trim() === "") return null;
  return toLdsDdMmYyyy(value) ?? normalizeSearchResultDate(value) ?? null;
}

/**
 * @param {URLSearchParams|Record<string, string>|null|undefined} urlSearchParams
 */
export function readHotelSearchDatesFromUrl(urlSearchParams) {
  let params = null;
  if (urlSearchParams instanceof URLSearchParams) {
    params = urlSearchParams;
  } else if (urlSearchParams && typeof urlSearchParams === "object") {
    params = new URLSearchParams(Object.entries(urlSearchParams));
  } else if (typeof window !== "undefined") {
    params = new URLSearchParams(window.location.search || "");
  }
  if (!params) {
    return { checkIn: null, checkOut: null };
  }
  return {
    checkIn: firstNonEmpty(
      params.get("dep_date"),
      params.get("checkIn"),
      params.get("check_in"),
      params.get("departure_date")
    ),
    checkOut: firstNonEmpty(
      params.get("ret_date"),
      params.get("checkOut"),
      params.get("check_out"),
      params.get("return_date")
    ),
  };
}

function payloadMatchesActiveRequest(lastSearchPayload, currentSearchRequestId) {
  if (!lastSearchPayload) return false;
  const hasDates = Boolean(lastSearchPayload.dep_date || lastSearchPayload.ret_date);
  if (!hasDates) return false;
  if (currentSearchRequestId == null) return true;
  if (lastSearchPayload.requestId == null) return true;
  return lastSearchPayload.requestId === currentSearchRequestId;
}

function resolveSourceUsed(resolved, payload, form, url, phrase) {
  if (!resolved) return "none";
  if (resolved === payload) return "lastSearchPayload";
  if (resolved === form) return "calendarDateArrival";
  if (resolved === url) return "urlParams";
  if (resolved === phrase) return "searchPhrase";
  return "unknown";
}

function resolveCheckOutSource(resolved, payload, form, url, phrase) {
  if (!resolved) return "none";
  if (resolved === payload) return "lastSearchPayload";
  if (resolved === form) return "calendarDateLeave";
  if (resolved === url) return "urlParams";
  if (resolved === phrase) return "searchPhrase";
  return "unknown";
}

/**
 * Single source of truth for active hotel-search dates across form, payload, URL and UI.
 *
 * Priority (never prefer stale cached phrase over live form):
 *   1. lastSearchPayload (when tied to current requestId)
 *   2. calendarDateArrival / calendarDateLeave (search form)
 *   3. URL params
 *   4. searchPhrase (fallback only)
 *
 * @param {{
 *   calendarDateArrival?: string|null,
 *   calendarDateLeave?: string|null,
 *   searchPhrase?: { checkIn?: string, checkOut?: string, dep_date?: string, ret_date?: string, depDate?: string, retDate?: string }|null,
 *   lastSearchPayload?: { requestId?: number|null, dep_date?: string|null, ret_date?: string|null }|null,
 *   currentSearchRequestId?: number|null,
 *   urlSearchParams?: URLSearchParams|Record<string, string>|null,
 * }} input
 */
export function resolveActiveHotelSearchDates(input = {}) {
  const {
    calendarDateArrival,
    calendarDateLeave,
    searchPhrase,
    lastSearchPayload,
    currentSearchRequestId,
    urlSearchParams,
  } = input;

  const formCheckIn = normalizeHotelSearchDisplayDate(calendarDateArrival);
  const formCheckOut = normalizeHotelSearchDisplayDate(calendarDateLeave);

  const urlRaw = readHotelSearchDatesFromUrl(urlSearchParams);
  const urlCheckIn = normalizeHotelSearchDisplayDate(urlRaw.checkIn);
  const urlCheckOut = normalizeHotelSearchDisplayDate(urlRaw.checkOut);

  const phraseCheckIn = normalizeHotelSearchDisplayDate(
    searchPhrase?.checkIn ?? searchPhrase?.dep_date ?? searchPhrase?.depDate
  );
  const phraseCheckOut = normalizeHotelSearchDisplayDate(
    searchPhrase?.checkOut ?? searchPhrase?.ret_date ?? searchPhrase?.retDate
  );

  const payloadActive = payloadMatchesActiveRequest(
    lastSearchPayload,
    currentSearchRequestId
  );
  const payloadCheckIn = payloadActive
    ? normalizeHotelSearchDisplayDate(lastSearchPayload?.dep_date)
    : null;
  const payloadCheckOut = payloadActive
    ? normalizeHotelSearchDisplayDate(lastSearchPayload?.ret_date)
    : null;

  const pick = (payload, form, url, phrase) =>
    firstNonEmpty(payload, form, url, phrase);

  const checkIn = pick(payloadCheckIn, formCheckIn, urlCheckIn, phraseCheckIn);
  const checkOut = pick(payloadCheckOut, formCheckOut, urlCheckOut, phraseCheckOut);

  return {
    checkIn,
    checkOut,
    sources: {
      checkIn: resolveSourceUsed(checkIn, payloadCheckIn, formCheckIn, urlCheckIn, phraseCheckIn),
      checkOut: resolveCheckOutSource(
        checkOut,
        payloadCheckOut,
        formCheckOut,
        urlCheckOut,
        phraseCheckOut
      ),
    },
    layers: {
      formCheckIn: calendarDateArrival ?? null,
      formCheckOut: calendarDateLeave ?? null,
      payloadCheckIn: lastSearchPayload?.dep_date ?? null,
      payloadCheckOut: lastSearchPayload?.ret_date ?? null,
      urlCheckIn: urlRaw.checkIn,
      urlCheckOut: urlRaw.checkOut,
      phraseCheckIn: searchPhrase?.checkIn ?? searchPhrase?.dep_date ?? null,
      phraseCheckOut: searchPhrase?.checkOut ?? searchPhrase?.ret_date ?? null,
    },
    requestId: currentSearchRequestId ?? null,
    payloadRequestId: lastSearchPayload?.requestId ?? null,
    payloadActive,
  };
}

/**
 * Production/staging trace for hotel search date SSOT alignment.
 * @param {Record<string, unknown>} payload
 */
export function logHotelSearchDatesTrace(payload) {
  try {
    // eslint-disable-next-line no-console
    console.info(HOTEL_SEARCH_DATES_TRACE, payload);
  } catch {
    /* ignore */
  }
}
